import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiAccordion,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiIcon,
  EuiLoadingChart,
  EuiMarkdownEditor,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSelect,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import {
  GitLabMerge,
  OpsanioPullPolicy,
  OpsanioVersion,
  OpsanioVersions,
  OpsanioWaves,
} from "src/questionnaires";
import {
  APT_RING_OPTIONS,
  defaultWaveData,
  OPSANIO_WAVES,
  PARENT_WAVE_OPTIONS,
  PULL_POLICY_OPTIONS,
} from "./consts";
import { useAnketaI18n } from "src/internationalization";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useApi } from "src/api";
import { useToast } from "src/notifications";
import { MergeRequestForm } from "./mergeRequest";
import {
  WaveFreshness,
  WaveLifecycle,
  WaveSummary,
  buildWaveSummaries,
  pickInitialWave,
} from "./model";
import { WaveChainRail } from "./rail";
import { WaveSummaryStats } from "./summary";
import { WaveDetailHeader } from "./detail";
import {
  WaveRootActionsPanel,
  WaveWorkflowPanel,
  WaveActionName,
} from "./workflow";
import { WaveMergePanel } from "./mergePanel";
import { WaveComponentTable } from "./components";

interface SaveStatus {
  type: "success" | "error";
  message: string;
}

/**
 * The wave update console.
 *
 * Master/detail: the promotion chain stays visible in a rail on the left while
 * one wave at a time gets the full detail treatment on the right (state, the
 * deployment workflow, the open update, the pinned components). When
 * `visibleWave` is set - the appliance flyout's Wave tab - the rail and the
 * fleet-wide tiles are dropped and only that wave's detail is rendered.
 */
export const WaveDeploymentTimeline = ({
  versions,
  merges,
  tags,
  updateAction,
  visibleWave,
  stages,
}: {
  versions: Record<OpsanioWaves, OpsanioVersions | null>;
  merges: Record<OpsanioWaves, GitLabMerge | null>;
  tags: any;
  updateAction?: () => Promise<void>;
  visibleWave?: OpsanioWaves;
  stages?: Record<OpsanioWaves, Array<string> | null>;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Put, Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();

  const [formData, setFormData] = useState<OpsanioVersions>(defaultWaveData);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [modalWave, setModalWave] = useState<OpsanioWaves | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // The wave for which the create-update / merge-request form is open (null =
  // closed). Scoped per-wave so opening it on one wave does not affect others.
  const [mergeFormWave, setMergeFormWave] = useState<OpsanioWaves | null>(null);

  // The wave whose "general information" notification composer is open (null =
  // closed), plus the operator-entered Markdown body and the in-flight flag.
  const [notifyWave, setNotifyWave] = useState<OpsanioWaves | null>(null);
  const [notifyBody, setNotifyBody] = useState<string>("");
  const [isNotifying, setIsNotifying] = useState<boolean>(false);

  // Lifecycle state per wave (locked / available / in_progress / closed),
  // derived from GitLab by the backend, plus the wave currently running a
  // workflow action so its control shows a spinner.
  const [lifecycle, setLifecycle] = useState<Record<
    OpsanioWaves,
    WaveLifecycle
  > | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);

  // Per-wave version freshness: whether a wave has absorbed every pinned version
  // from its parent. Drives the ordered-promotion gate, mirroring the
  // server-side "409 Parent wave not up to date" response.
  const [freshness, setFreshness] = useState<Record<
    OpsanioWaves,
    WaveFreshness
  > | null>(null);

  const [selectedWave, setSelectedWave] = useState<OpsanioWaves | null>(null);

  const loadLifecycle = useCallback(async () => {
    if (!keycloak?.token) return;
    try {
      const { data } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/waves/lifecycle`,
        responseType: "json",
        token: keycloak.token,
      });
      if (data) {
        setLifecycle(data as Record<OpsanioWaves, WaveLifecycle>);
      }
    } catch (err) {
      console.error("Failed to load wave lifecycle:", err);
    }
  }, [keycloak?.token]);

  const loadFreshness = useCallback(async () => {
    if (!keycloak?.token) return;
    try {
      const entries = await Promise.all(
        OPSANIO_WAVES.map(async (wave) => {
          try {
            const { data } = await Get({
              path: `v2/${AnketaResourcesEnum.opsanio}/waves/${wave}/version-freshness`,
              responseType: "json",
              token: keycloak.token,
            });
            // The endpoint wraps the payload as { status, data }; unwrap it
            // (tolerate a raw object too) before storing.
            const fresh = ((data as any)?.data ?? data) as WaveFreshness | null;
            return [wave, fresh] as const;
          } catch {
            return [wave, null] as const;
          }
        }),
      );
      const map = {} as Record<OpsanioWaves, WaveFreshness>;
      for (const [wave, data] of entries) if (data) map[wave] = data;
      setFreshness(map);
    } catch (err) {
      console.error("Failed to load wave version freshness:", err);
    }
  }, [keycloak?.token]);

  useEffect(() => {
    loadLifecycle();
    loadFreshness();
  }, [loadLifecycle, loadFreshness]);

  const summaries = useMemo(
    () =>
      buildWaveSummaries({
        waves: OPSANIO_WAVES,
        versions,
        merges,
        tags,
        appliances: stages ?? null,
        lifecycle,
        freshness,
      }),
    [versions, merges, tags, stages, lifecycle, freshness],
  );

  // Land the operator on whatever needs them most, but never override a wave
  // they have since picked (or the single wave the flyout pins us to).
  useEffect(() => {
    if (visibleWave || selectedWave || merges === null) return;
    setSelectedWave(pickInitialWave(summaries));
  }, [visibleWave, selectedWave, merges, summaries]);

  const activeWave = visibleWave ?? selectedWave ?? OPSANIO_WAVES[0];
  const active =
    summaries.find((s) => s.wave === activeWave) ?? summaries[0];
  const parentVersions = active?.parentWave
    ? (versions?.[active.parentWave] ?? null)
    : null;

  /**
   * Trigger a wave-workflow action and surface the outcome via a toast. Each is
   * a distinct endpoint on the wave; `merge` is gated server-side on the
   * approval state and returns a 409 reason when the MR is not yet approved.
   */
  const runWaveAction = async (
    wave: OpsanioWaves,
    action: WaveActionName,
    label: string,
  ): Promise<void> => {
    if (!keycloak?.token) return;
    setActionInFlight(`${wave}:${action}`);
    try {
      const { data, error } = await Post({
        path: `v2/${AnketaResourcesEnum.opsanio}/waves/${wave}/${action}`,
        value: {},
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.waves.action.failed", {
            values: { label },
            defaultMessage: "{{label}} failed",
          }),
          color: "danger",
          text:
            (error as any)?.details ||
            error.message ||
            translate("anketa.opsanio.waves.action.unknown_error", {
              defaultMessage: "Unknown error",
            }),
        });
      } else {
        addToast({
          title: translate("anketa.opsanio.waves.action.triggered", {
            values: { label },
            defaultMessage: "{{label}} triggered",
          }),
          color: "success",
          text: (data as any)?.message || "",
        });
        await loadLifecycle();
        await loadFreshness();
        if (updateAction) await updateAction();
      }
    } catch (err) {
      addToast({
        title: translate("anketa.opsanio.waves.action.failed", {
          values: { label },
          defaultMessage: "{{label}} failed",
        }),
        color: "danger",
        text: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setActionInFlight(null);
    }
  };

  // Send the "general information" notification for the open compose wave: POSTs
  // the Markdown body to the notify endpoint, which fans out one dedicated mail
  // per appliance (To its update-notification address, technical + sales in CC).
  const runNotify = async (): Promise<void> => {
    if (!keycloak?.token || !notifyWave) return;
    if (notifyBody.trim() === "") {
      addToast({
        title: translate("anketa.opsanio.waves.notify.empty", {
          defaultMessage: "Please enter a message before sending.",
        }),
        color: "warning",
        text: "",
      });
      return;
    }
    setIsNotifying(true);
    setActionInFlight(`${notifyWave}:notify`);
    try {
      const { data, error } = await Post({
        path: `v2/${AnketaResourcesEnum.opsanio}/waves/${notifyWave}/notify`,
        value: { body: notifyBody },
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.waves.notify.failed", {
            defaultMessage: "Notification failed",
          }),
          color: "danger",
          text:
            (error as any)?.details ||
            error.message ||
            translate("anketa.opsanio.waves.action.unknown_error", {
              defaultMessage: "Unknown error",
            }),
        });
      } else {
        addToast({
          title: translate("anketa.opsanio.waves.notify.sent", {
            defaultMessage: "Notification sent",
          }),
          color: "success",
          text: (data as any)?.message || "",
        });
        setNotifyWave(null);
        setNotifyBody("");
      }
    } catch (err) {
      addToast({
        title: translate("anketa.opsanio.waves.notify.failed", {
          defaultMessage: "Notification failed",
        }),
        color: "danger",
        text: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setIsNotifying(false);
      setActionInFlight(null);
    }
  };

  const openModal = (wave: OpsanioWaves): void => {
    setFormData(versions[wave] ?? defaultWaveData);
    setModalWave(wave);
    setIsModalVisible(true);
    setSaveStatus(null);
  };

  const closeModal = (): void => {
    setIsModalVisible(false);
    setModalWave(null);
    setSaveStatus(null);
  };

  const updateFormField = (
    field: keyof OpsanioVersions,
    value: string,
  ): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (): Promise<void> => {
    if (!modalWave) return;

    setIsSaving(true);
    setSaveStatus(null);
    try {
      const { error } = await Put({
        path: `v2/${AnketaResourcesEnum.opsanio}/versions/${modalWave}`,
        value: {
          ...formData,
          opsanio_plugin_tag: `${formData.opensearch_dashboard_version}-${formData.wazuh_version}-${formData.opsanio_plugin_version}`,
        },
        token: keycloak.token,
      });

      if (error) {
        console.error(error);
        addToast({
          title: translate("anketa.opsanio.waves.put.error.title", {
            defaultMessage: "Fehler beim updaten der Version!",
          }),
          color: "danger",
          text: error.message,
        });
        setSaveStatus({
          type: "error",
          message: translate("anketa.opsanio.error", {
            values: { error: error.message || "Unknown" },
          }),
        });
      } else {
        if (updateAction) await updateAction();
        addToast({
          title: translate("anketa.opsanio.waves.put.successes.title", {
            defaultMessage: "Version wurde geupdated!",
          }),
          color: "success",
          text: "",
        });
        setSaveStatus({
          type: "success",
          message: translate("anketa.opsanio.success", {
            defaultMessage: "Version erfolgreich aktualisiert!",
          }),
        });
        setTimeout(() => closeModal(), 1500);
      }
    } catch (err) {
      setSaveStatus({
        type: "error",
        message: translate("anketa.opsanio.error.connection", {
          defaultMessage:
            "Verbindungsfehler. Bitte überprüfen Sie die Server-Verbindung.",
        }),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateWaveVersion = async (
    wave: OpsanioVersion,
    data: OpsanioVersions,
    versionToBump: keyof OpsanioVersion,
    newVersion: string,
  ): Promise<void> => {
    const updatedObject = {
      ...data,
      [versionToBump]: newVersion,
      opsanio_plugin_tag: `${data.opensearch_dashboard_version}-${data.wazuh_version}-${(data.opsanio_plugin_version ?? "").replace(/^v/, "")}`,
    };
    try {
      const { error } = await Put({
        path: `v2/${AnketaResourcesEnum.opsanio}/versions/${wave}`,
        value: updatedObject,
        token: keycloak.token,
      });

      if (error) {
        console.error(error);
        addToast({
          title: translate("anketa.opsanio.waves.put.error.title", {
            defaultMessage: "Fehler beim updaten der Version!",
          }),
          color: "danger",
          text: error.message,
        });
      } else if (updateAction) {
        await updateAction();
      }
    } catch (err) {
      addToast({
        title: translate("anketa.opsanio.waves.put.error.title", {
          defaultMessage: "Fehler beim updaten der Version!",
        }),
        color: "danger",
        text: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const renderFormFields = (): JSX.Element => {
    const coreFields: (keyof OpsanioVersions)[] = [
      "pullPolicy",
      "parentWave",
      "opensearch_dashboard_version",
      "opensearch_version",
      "wazuh_version",
      "opsanio_plugin_version",
      "opsanio_plugin_tag",
    ];

    const infrastructureFields: (keyof OpsanioVersions)[] = [
      "nats_processor_version",
      "nats_version",
      "memgraph_version",
      "mage_version",
      "coredns_version",
      "promtail_version",
      "cert_manager",
      "longhorn",
    ];

    const wodleFields: (keyof OpsanioVersions)[] = [
      "kubernetes_wodle",
      "remotelog_wodle",
      "imap_wodle",
      "osp_wodle",
      "promtail_wodle",
      "snmp_wodle",
      "webhook_wodle",
      "netflow_wodle",
      "wazuh_rules",
      "semaphrone",
      "codex",
      "most",
      "cardea",
      "apt_ring",
    ];

    const renderField = (field: keyof OpsanioVersions): JSX.Element => {
      if (field === "pullPolicy") {
        return (
          <EuiFormRow
            key={field}
            label={translate(`anketa.opsanio.field.${field}`, {
              defaultMessage: field,
            })}
            fullWidth
          >
            <EuiSelect
              options={PULL_POLICY_OPTIONS}
              value={formData[field]}
              onChange={(e) =>
                updateFormField(field, e.target.value as OpsanioPullPolicy)
              }
              fullWidth
            />
          </EuiFormRow>
        );
      }
      if (field === "parentWave") {
        return (
          <EuiFormRow
            key={field}
            label={translate(`anketa.opsanio.field.${field}`, {
              defaultMessage: field,
            })}
            fullWidth
          >
            <EuiSelect
              options={PARENT_WAVE_OPTIONS}
              value={formData[field]}
              onChange={(e) =>
                updateFormField(field, e.target.value as OpsanioWaves)
              }
              fullWidth
            />
          </EuiFormRow>
        );
      }
      if (field === "apt_ring") {
        // A select, never free text: the ring is the OCI tag most pulls, and
        // a typo does not fail - the appliance just starts unpinned.
        return (
          <EuiFormRow
            key={field}
            label={translate(`anketa.opsanio.field.${field}`, {
              defaultMessage: field,
            })}
            helpText={translate("anketa.opsanio.field.apt_ring.help", {
              defaultMessage:
                "Only applies to appliances with the feature \"Pinned APT suite\" enabled.",
            })}
            fullWidth
          >
            <EuiSelect
              options={APT_RING_OPTIONS}
              value={formData[field] ?? ""}
              onChange={(e) => updateFormField(field, e.target.value)}
              fullWidth
            />
          </EuiFormRow>
        );
      }
      if (field === "opsanio_plugin_tag") {
        let version = `${formData.opensearch_dashboard_version}-${formData.wazuh_version}-${formData.opsanio_plugin_version}`;
        if (
          formData.opsanio_plugin_version === "test" ||
          formData.opsanio_plugin_version === "latest"
        ) {
          version = formData.opsanio_plugin_version;
        }
        return (
          <EuiFormRow
            key={field}
            label={translate(`anketa.opsanio.field.${field}`, {
              defaultMessage: field,
            })}
            fullWidth
          >
            <EuiFieldText value={version} disabled fullWidth />
          </EuiFormRow>
        );
      }
      return (
        <EuiFormRow
          key={field}
          label={translate(`anketa.opsanio.field.${field}`, {
            defaultMessage: field,
          })}
          fullWidth
        >
          <EuiFieldText
            value={formData[field] as string}
            onChange={(e) => updateFormField(field, e.target.value)}
            fullWidth
          />
        </EuiFormRow>
      );
    };

    return (
      <>
        <EuiAccordion
          id="core"
          buttonContent={translate("anketa.opsanio.category.core", {
            defaultMessage: "Core Komponenten",
          })}
          initialIsOpen
        >
          <EuiSpacer size="m" />
          {coreFields.map(renderField)}
        </EuiAccordion>
        <EuiSpacer />
        <EuiAccordion
          id="infrastructure"
          buttonContent={translate("anketa.opsanio.category.infrastructure", {
            defaultMessage: "Infrastruktur",
          })}
          initialIsOpen={false}
        >
          <EuiSpacer size="m" />
          {infrastructureFields.map(renderField)}
        </EuiAccordion>
        <EuiSpacer />
        <EuiAccordion
          id="wodles"
          buttonContent={translate("anketa.opsanio.category.wodles", {
            defaultMessage: "Wodles",
          })}
          initialIsOpen={false}
        >
          <EuiSpacer size="m" />
          {wodleFields.map(renderField)}
        </EuiAccordion>
      </>
    );
  };

  /**
   * Chain-level inconsistencies worth interrupting for: a wave deployed ahead of
   * its parent, or configured while its parent has no version set at all.
   */
  const flowWarnings = useMemo((): string[] => {
    const warnings: string[] = [];
    summaries.forEach((s, index) => {
      if (index === 0 || !s.parentWave) return;
      const parent = summaries[index - 1];
      if (s.version && !parent.version) {
        warnings.push(
          translate("anketa.opsanio.waves.flow.missing_parent", {
            values: { wave: s.wave, parent: parent.wave },
            defaultMessage: "{{wave}} is configured, but {{parent}} is missing!",
          }),
        );
      }
      if (s.isMerged && !parent.isMerged && parent.wave !== "dev") {
        warnings.push(
          translate("anketa.opsanio.waves.flow.parent_not_deployed", {
            values: { wave: s.wave, parent: parent.wave },
            defaultMessage: "{{wave}} is deployed, but {{parent}} is not deployed!",
          }),
        );
      }
    });
    return warnings;
  }, [summaries, translate]);

  const renderDetail = (summary: WaveSummary): JSX.Element => (
    <EuiFlexGroup direction="column" gutterSize="m">
      <EuiFlexItem grow={false}>
        <WaveDetailHeader summary={summary} />
      </EuiFlexItem>
      {summary.state === "root" ? (
        <EuiFlexItem grow={false}>
          <WaveRootActionsPanel onEdit={() => openModal(summary.wave)} />
        </EuiFlexItem>
      ) : (
        <EuiFlexItem grow={false}>
          <WaveWorkflowPanel
            summary={summary}
            actionInFlight={actionInFlight}
            onCreateUpdate={() => setMergeFormWave(summary.wave)}
            onEdit={() => openModal(summary.wave)}
            onNotify={() => {
              setNotifyBody("");
              setNotifyWave(summary.wave);
            }}
            runWaveAction={runWaveAction}
          />
        </EuiFlexItem>
      )}
      {summary.merge && (
        <EuiFlexItem grow={false}>
          <WaveMergePanel
            summary={summary}
            initiallyOpen={visibleWave !== undefined}
          />
        </EuiFlexItem>
      )}
      <EuiFlexItem grow={false}>
        <WaveComponentTable
          summary={summary}
          parentVersions={parentVersions}
          tags={tags}
          updateWaveVersion={updateWaveVersion}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const modals = (
    <>
      {isModalVisible && modalWave && (
        <EuiModal onClose={closeModal} style={{ width: 700, maxWidth: "90vw" }}>
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <EuiFlexGroup gutterSize="s" alignItems="center">
                <EuiFlexItem grow={false}>
                  <EuiIcon type="pencil" size="l" />
                </EuiFlexItem>
                <EuiFlexItem>
                  {translate("anketa.opsanio.modal.title", {
                    values: { wave: modalWave },
                    defaultMessage: `${modalWave} bearbeiten`,
                  })}
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            {saveStatus && (
              <>
                <EuiCallOut
                  title={saveStatus.message}
                  color={saveStatus.type === "success" ? "success" : "danger"}
                  iconType={saveStatus.type === "success" ? "check" : "alert"}
                />
                <EuiSpacer />
              </>
            )}
            <EuiForm component="form">{renderFormFields()}</EuiForm>
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={closeModal}>
              {translate("anketa.opsanio.modal.cancel", {
                defaultMessage: "Abbrechen",
              })}
            </EuiButtonEmpty>
            <EuiButton onClick={handleSave} fill isLoading={isSaving}>
              {translate("anketa.opsanio.modal.save", {
                defaultMessage: "Speichern",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}

      {notifyWave && (
        <EuiModal
          onClose={() => setNotifyWave(null)}
          style={{ width: 700, maxWidth: "90vw" }}
        >
          <EuiModalHeader>
            <EuiModalHeaderTitle>
              <EuiFlexGroup gutterSize="s" alignItems="center">
                <EuiFlexItem grow={false}>
                  <EuiIcon type="email" size="l" />
                </EuiFlexItem>
                <EuiFlexItem>
                  {translate("anketa.opsanio.waves.notify.title", {
                    values: { wave: notifyWave },
                    defaultMessage: `Notify appliances on "${notifyWave}"`,
                  })}
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiText size="s" color="subdued">
              {translate("anketa.opsanio.waves.notify.description", {
                defaultMessage:
                  'One dedicated email is sent per appliance on this wave: To its update-notification address, with the technical and sales contacts in CC. Subject: "opsanio - Generelle Information".',
              })}
            </EuiText>
            <EuiSpacer size="m" />
            <EuiMarkdownEditor
              aria-label={translate("anketa.opsanio.waves.notify.editor_label", {
                defaultMessage: "Notification message (Markdown)",
              })}
              placeholder={translate("anketa.opsanio.waves.notify.placeholder", {
                defaultMessage: "Write your message… (Markdown supported)",
              })}
              value={notifyBody}
              onChange={setNotifyBody}
              height={300}
            />
          </EuiModalBody>
          <EuiModalFooter>
            <EuiButtonEmpty onClick={() => setNotifyWave(null)}>
              {translate("anketa.opsanio.modal.cancel", {
                defaultMessage: "Abbrechen",
              })}
            </EuiButtonEmpty>
            <EuiButton
              fill
              iconType="email"
              onClick={runNotify}
              isLoading={isNotifying}
              isDisabled={notifyBody.trim() === ""}
            >
              {translate("anketa.opsanio.waves.notify.send", {
                defaultMessage: "Send notification",
              })}
            </EuiButton>
          </EuiModalFooter>
        </EuiModal>
      )}

      {mergeFormWave && (
        <MergeRequestForm
          onClose={() => {
            if (updateAction) updateAction();
            setMergeFormWave(null);
          }}
          sourceBranch={
            versions[mergeFormWave]?.parentWave ??
            OPSANIO_WAVES[OPSANIO_WAVES.indexOf(mergeFormWave) - 1] ??
            "dev"
          }
          targetBranch={mergeFormWave}
        />
      )}
    </>
  );

  if (merges === null) {
    return (
      <EuiFlexGroup
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: 240 }}
      >
        <EuiFlexItem grow={false}>
          <EuiLoadingChart size="xl" />
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  }

  // Flyout mode: a single wave, no chain rail and no fleet-wide tiles.
  if (visibleWave) {
    return (
      <>
        {active && renderDetail(active)}
        {modals}
      </>
    );
  }

  return (
    <>
      <WaveSummaryStats summaries={summaries} selected={active} />

      {flowWarnings.length > 0 && (
        <>
          <EuiSpacer size="m" />
          <EuiCallOut
            title={translate("anketa.opsanio.workflow.warning.title", {
              defaultMessage: "Workflow-Warnung",
            })}
            color="warning"
            iconType="alert"
            size="s"
          >
            <ul>
              {flowWarnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </EuiCallOut>
        </>
      )}

      <EuiSpacer size="l" />

      <EuiFlexGroup gutterSize="l" alignItems="flexStart">
        <EuiFlexItem grow={false} style={{ width: 340, position: "sticky", top: 16 }}>
          <WaveChainRail
            summaries={summaries}
            selected={activeWave}
            onSelect={setSelectedWave}
          />
        </EuiFlexItem>
        <EuiFlexItem style={{ minWidth: 0 }}>
          {active && renderDetail(active)}
        </EuiFlexItem>
      </EuiFlexGroup>

      {modals}
    </>
  );
};
